// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions, { ProductActionsStyle } from "@ces/App/Components/Product/ProductActions";
import ProductQtyInfo from "@ces/App/Components/Product/ProductQtyInfo";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    // products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    products: ProductContextModel[];
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Change
    erpNumberValue?: TypographyPresentationProps;
    erpWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    actionsContainer?: GridItemProps;
    productActions?: ProductActionsStyle;
    productsContainer?: InjectableCss;
    productDetailsContainer?: InjectableCss;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        // XNG-change: Add count, and container
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}&nbsp;&nbsp;({products.length})
                </Typography>
                {/* XNG-Change: on click just for title add wrapper */}
                <StyledWrapper {...styles.productsContainer}>
                    {products.map(({ product, ...context }) => (
                        <ProductContext.Provider value={{ product, ...context }} key={product.id!}>
                            <GridContainer
                                {...(this.props.focusedItem?.id === product.id
                                    ? styles.focusedContainer
                                    : styles.container)}
                                key={product.id!}
                                // onClick={() => {
                                //     this.props.goToUrl(product.url);
                                // }}
                                className="autocomplete-products-container"
                            >
                                <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                                    <Clickable
                                        onClick={() => {
                                            this.props.goToUrl(context.productInfo.productDetailPath);
                                        }}
                                        data-test-selector="productImage"
                                    >
                                        <LazyImage
                                            {...styles.image}
                                            src={product.mediumImagePath || product.smallImagePath}
                                            className="autocomplete-products-image"
                                        />
                                    </Clickable>
                                </GridItem>
                                <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                    <Link
                                        {...styles.titleLink}
                                        data-test-selector={`productId_${product.id}`}
                                        className="autocomplete-products-link"
                                        onClick={() => {
                                            this.props.goToUrl(context.productInfo.productDetailPath);
                                        }}
                                    >
                                        {product.productTitle}
                                    </Link>
                                    {/* XNG-Change show sku */}
                                    <StyledWrapper {...styles.productDetailsContainer}>
                                        <StyledWrapper>
                                            <Typography
                                                {...styles.erpNumberText}
                                                data-test-selector="autocompleteProducts_PartNumber"
                                                className="autocomplete-products-erp-number"
                                            >
                                                {translate("SKU #")}
                                            </Typography>
                                            <Typography
                                                {...styles.erpNumberValue}
                                                data-test-selector="autocompleteProducts_PartNumber"
                                                className="autocomplete-products-erp-number"
                                            >
                                                {product.productNumber}
                                            </Typography>
                                        </StyledWrapper>
                                        <ProductQtyInfo
                                            product={product}
                                            showPlaceholder={false}
                                            {...{ selectedUOMLineId: `Auto-Complete-${product.id}` }}
                                        />
                                        <ProductActions extendedStyles={styles.productActions} />
                                    </StyledWrapper>
                                    {/* <Typography
                                    {...styles.erpNumberText}
                                    data-test-selector="autocompleteProducts_PartNumber"
                                    className="autocomplete-products-erp-number"
                                >
                                    {product.isNameCustomerOverride ? (
                                        <>
                                            {translate("My Part #")}
                                            {product.displayName}
                                        </>
                                    ) : (
                                        <>
                                            {translate("Part #")}
                                            {product.displayErpNumber}
                                        </>
                                    )}
                                    {product.manufacturerItemNumber && (
                                        <>
                                            &nbsp;
                                            {translate("MFG #")}
                                            {product.displayManufacturerItemNumber}
                                        </>
                                    )}
                                </Typography> */}
                                </GridItem>
                            </GridContainer>
                        </ProductContext.Provider>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

export default AutocompleteProducts;
